import { useRef } from 'react';
import clsx from 'clsx';
import { ALL_KNOWN_DETAILS_TESTIDS, KNOWN_DETAILS_PANEL_ID, KNOWN_DETAIL_TAB_LABEL, KNOWN_DETAIL_TAB_ORDER } from '../../constants';
import { deriveTabCount, padTabCount } from '../../utils/allKnownDetailsUtils';
import type { AllKnownDetailsData, KnownDetailType } from '../../types';
import styles from './KnownDetailsTabBar.module.scss';

interface KnownDetailsTabBarProps {
    data: AllKnownDetailsData | null;
    activeType: KnownDetailType;
    onSelect: (type: KnownDetailType) => void;
}

const nextIndexFor = (key: string, current: number, length: number): number | null => {
    if (key === 'ArrowRight') return (current + 1) % length;
    if (key === 'ArrowLeft') return (current - 1 + length) % length;
    if (key === 'Home') return 0;
    if (key === 'End') return length - 1;
    return null;
};

export const tabIdFor = (type: KnownDetailType) => `${ALL_KNOWN_DETAILS_TESTIDS.TAB}-${type}`;

/** Roving tabindex: arrow keys, Home and End move selection and focus together. */
const KnownDetailsTabBar = ({ data, activeType, onSelect }: KnownDetailsTabBarProps) => {
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        const currentIndex = KNOWN_DETAIL_TAB_ORDER.indexOf(activeType);
        const nextIndex = nextIndexFor(event.key, currentIndex, KNOWN_DETAIL_TAB_ORDER.length);
        if (nextIndex === null) return;
        event.preventDefault();
        const nextType = KNOWN_DETAIL_TAB_ORDER[nextIndex];
        onSelect(nextType);
        tabRefs.current[nextType]?.focus();
    };

    return (
        <div className={styles.TabBar}>
            <div className={styles.TabList} role="tablist" data-testid={ALL_KNOWN_DETAILS_TESTIDS.TAB_BAR}>
                {KNOWN_DETAIL_TAB_ORDER.map((type) => {
                    const isActive = type === activeType;
                    return (
                        <button
                            key={type}
                            id={tabIdFor(type)}
                            ref={(node) => {
                                tabRefs.current[type] = node;
                            }}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={KNOWN_DETAILS_PANEL_ID}
                            tabIndex={isActive ? 0 : -1}
                            data-testid={ALL_KNOWN_DETAILS_TESTIDS.TAB}
                            className={clsx(styles.Tab, { [styles.TabActive]: isActive })}
                            onClick={() => onSelect(type)}
                            onKeyDown={handleKeyDown}
                        >
                            <span className={styles.TabLabel}>{KNOWN_DETAIL_TAB_LABEL[type]}</span>
                            <span className={styles.TabCount}>{padTabCount(deriveTabCount(data, type))}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default KnownDetailsTabBar;
